import React, { useMemo, useState, useEffect } from 'react'
import RosterTable, { rosterTotals } from './RosterTable'
import { C, ui } from '../theme'

const btn = ui.btn
const btnGhost = ui.btnGhost

// The "Army Building sheet" made native: every troop type is a row, you type a count, totals
// update live, one Save writes the whole roster. Pre-fills from the army's current pools, so
// the same grid both builds a new army and edits an existing one. Now a DM tool: when `isDm`
// the DM may tick "charge treasury" so net additions are paid at the reserve-replenishment rate.
export default function RosterGrid({ catalog = [], army, canAct, isDm, onSave }) {
  // current men per code from the army's live pools (sum across same-code pools)
  const current = useMemo(() => {
    const m = {}
    for (const p of (army?.pools || [])) if (!p.disbanded) m[p.troop_code] = (m[p.troop_code] || 0) + p.men
    return m
  }, [army])

  // The effective row list = the catalog PLUS any pool the army already has whose code isn't
  // in the catalog (legacy/imported units), so the grid never hides an army's actual troops.
  const rowsCatalog = useMemo(() => {
    const codes = new Set(catalog.map(t => t.code))
    const orphans = []
    for (const p of (army?.pools || [])) {
      if (p.disbanded || codes.has(p.troop_code)) continue
      if (orphans.some(o => o.code === p.troop_code)) continue
      orphans.push({ code: p.troop_code, name: p.troop_name, wage_per_man: p.wage_per_man, supply_size: p.supply_size, mount_supply_size: p.mount_supply_size })
    }
    return [...orphans, ...catalog]
  }, [catalog, army])

  const [counts, setCounts] = useState(current)
  const [charge, setCharge] = useState(false)
  const [savedFlash, setSavedFlash] = useState(false)
  // Signature of the army's actual (non-disbanded) pools. Re-seed the grid only when THIS army's
  // identity or pools genuinely change — switching armies, or a save/remote edit to this army —
  // NOT on every parent refresh (the live-sync poll rebuilds the armies array every few seconds,
  // which would otherwise wipe an in-progress edit).
  const poolSig = useMemo(
    () => JSON.stringify((army?.pools || []).filter(p => !p.disbanded).map(p => [p.troop_code, p.men]).sort()),
    [army]
  )
  useEffect(() => { setCounts(current) }, [army?.id, poolSig]) // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(
    () => rowsCatalog.some(t => (Number(counts[t.code]) || 0) !== (current[t.code] || 0)),
    [counts, current, rowsCatalog]
  )

  const totals = useMemo(() => rosterTotals(rowsCatalog, counts), [counts, rowsCatalog])
  // gp to charge if "charge treasury" is on: net men ADDED × wage × 10 (reserve-replenishment rate)
  const addCost = useMemo(() => {
    if (!charge) return 0
    let c = 0
    for (const t of rowsCatalog) {
      const added = Math.max(0, (Number(counts[t.code]) || 0) - (current[t.code] || 0))
      c += added * (t.wage_per_man || 0) * 10
    }
    return c
  }, [charge, counts, current, rowsCatalog])

  const save = async () => {
    const rows = rowsCatalog
      .filter(t => (Number(counts[t.code]) || 0) !== (current[t.code] || 0))
      .map(t => ({ troopCode: t.code, men: Number(counts[t.code]) || 0 }))
    if (!rows.length) return
    const res = await onSave(rows, charge)
    if (!res || res.ok) { setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1200) }
  }

  // show types the army already has first, then the rest of the catalog
  const ordered = useMemo(() => {
    const has = rowsCatalog.filter(t => current[t.code])
    const rest = rowsCatalog.filter(t => !current[t.code])
    return [...has, ...rest]
  }, [rowsCatalog, current])

  return (
    <div>
      <RosterTable rows={ordered} counts={counts} canEdit={canAct}
        onChange={(code, v) => setCounts(c => ({ ...c, [code]: v }))} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, fontSize: 12, opacity: 0.85 }}>
        <span><b>{totals.men.toLocaleString()}</b> men</span>
        <span>· {Math.round(totals.wage).toLocaleString()} gp/day wages</span>
        <span>· {Math.round(totals.supply).toLocaleString()} gp/day supply</span>
      </div>
      {canAct && (
        <>
          {isDm && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12, cursor: 'pointer', color: C.inkSoft }}>
              <input type="checkbox" checked={charge} onChange={e => setCharge(e.target.checked)} />
              Charge treasury for added troops {charge && addCost > 0 && <b style={{ color: C.gold }}>· {Math.round(addCost).toLocaleString()} gp</b>}
            </label>
          )}
          <button style={{ ...(dirty ? btn : btnGhost), marginTop: 8, width: '100%' }} disabled={!dirty} onClick={save}>
            {savedFlash ? 'Saved ✓' : dirty ? 'Save roster' : 'Roster saved'}
          </button>
        </>
      )}
    </div>
  )
}
